import clsx from 'clsx'
import React, {
  Children,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import type {
  ChangeEventHandler,
  Dispatch,
  JSXElementConstructor,
  MouseEventHandler,
  MutableRefObject,
  PropsWithChildren,
  ReactElement,
  Ref,
  RefObject,
  SetStateAction,
} from 'react'
import { useAutocomplete } from '../Autocomplete/Autocomplete'
import Checkbox from '../Checkbox/Checkbox'
import { useDisposables } from '../hooks/useDisposables'
import { useId } from '../hooks/useId'
import ChevronRight from '../icons/ChevronRight'
import Done from '../icons/Done'
import type SvgIcon from '../SvgIcon/SvgIcon'
import Typography from '../Typography/Typography'
import { Focus } from '../utilities/calculateActiveIndex'
import createDisposables from '../utilities/createDisposables'
import { toDataSet } from '../utilities/toDataSet'
import { ActionTypes } from './Actions'
import Menu, { useMenu } from './Menu'

export type SubmenuActions =
  | { type: SubmenuActionTypes.CloseAll }
  | { type: SubmenuActionTypes.CloseMenu }
  | { type: SubmenuActionTypes.OpenMenu }
  | { type: SubmenuActionTypes.RegisterMenu; id: string }
  | { type: SubmenuActionTypes.UnregisterMenu }

export enum SubmenuActionTypes {
  CloseAll,

  OpenMenu,
  CloseMenu,

  RegisterMenu,
  UnregisterMenu,
}

type SubmenuReducers = {
  [ActionType in SubmenuActionTypes]: (action: SubmenuActions) => void
}

type SubmenuState = {
  id: string
  ref: Ref<HTMLButtonElement>
  show: boolean | undefined
}

type UseSubmenuValues = [SubmenuState, Dispatch<SubmenuActions>]

const SubmenuContext = createContext<
  [SubmenuState, Dispatch<SubmenuActions>] | null
>(null)

export const useSubmenu = (): UseSubmenuValues =>
  useContext(SubmenuContext) as UseSubmenuValues

type StartAdornmentProps =
  | {
      /**
       * Whether to render the item as a checkbox
       */
      checkbox?: never
      /**
       * The color of the checkbox
       */
      color?: never
      /**
       * Element to display at the start of the input.
       * Ignored when the item is rendered as a checkbox
       */
      startAdornment?: JSXElementConstructor<unknown> | ReactElement
    }
  | {
      /**
       * Whether to render the item as a checkbox
       */
      checkbox?: true
      /**
       * The color of the checkbox
       */
      color?: ItemColor
      /**
       * Element to display at the start of the input.
       * Ignored when the item is rendered as a checkbox
       */
      startAdornment?: never
    }

export type ItemProps = {
  /**
   * Whether activating the item will perform an action as opposed to selecting that item
   * @default false
   */
  action?: boolean
  /**
   * Reference to an element that should receive focus when the item becomes active
   */
  applyFocusRef?: RefObject<HTMLElement>
  /**
   * Accessible label for the menu item
   */
  'aria-label'?: string
  /**
   * ID(s) of element(s) whose text content labels this menu item for screen readers.
   * When provided, overrides the button's computed accessible name.
   */
  'aria-labelledby'?: string
  /**
   * Used for column filters in Table component
   */
  'data-key'?: string
  /**
   * Custom data attributes to be added to the item element
   */
  dataSet?: Record<string, string | undefined>
  /**
   * Initial checked state when the item is rendered as a checkbox
   * @default false
   */
  defaultChecked?: boolean
  /**
   * If true, the item is disabled
   * @default false
   */
  disabled?: boolean
  /**
   * Icon to display at the end of the item
   */
  endAdornment?: typeof SvgIcon
  /**
   * Whether to indent the item content (typically for alignment with checkboxes)
   * @default false
   */
  inset?: boolean
  /**
   * Callback function invoked when the checkbox state changes
   */
  onChange?: ChangeEventHandler<HTMLInputElement>
  /**
   * Callback fired when the item is activated
   */
  onClick?: MouseEventHandler<HTMLButtonElement>
  /**
   * Whether activating the item should trigger selection and close the menu
   * @default true
   */
  selectable?: boolean
  /**
   * Whether the item is currently selected
   */
  selected?: boolean
  /**
   * Value associated with the item
   */
  value?: string
  /**
   * Whether to wrap long option text
   */
  wrapText?: boolean
} & StartAdornmentProps

type ItemColor = 'error' | 'primary' | 'secondary'

export type MenuItemDataRef = MutableRefObject<{
  checkbox?: boolean
  disabled: boolean
  hasSubmenu: boolean
  textValue?: string
}>

function Item(
  {
    action = false,
    applyFocusRef,
    'aria-label': label,
    'aria-labelledby': labelledBy,
    checkbox,
    children,
    color = 'primary',
    'data-key': key,
    dataSet,
    defaultChecked = false,
    disabled = false,
    endAdornment,
    inset = false,
    onChange,
    onClick,
    selectable = true,
    selected,
    startAdornment: StartAdornment,
    value,
    wrapText = false,
  }: PropsWithChildren<ItemProps>,
  ref: Ref<HTMLButtonElement>,
): JSX.Element {
  const autocomplete = useAutocomplete()
  const d = useDisposables()
  const [state, dispatch] = useMenu()

  const [submenuId, setSubmenuId] = useState(undefined) as [
    string | undefined,
    Dispatch<SetStateAction<string | undefined>>,
  ]

  const [checked, toggleChecked] = useReducer((state: boolean) => {
    return !state
  }, defaultChecked)

  const id = `coconut-menu-item-${useId()}`

  const checkboxRef = useRef<HTMLInputElement | null>(null)
  const itemRef = useRef<HTMLButtonElement | null>(null)
  ref = ref || itemRef

  const showSubmenu = id === state.openSubmenuItemId
  const hasSubmenu = useMemo(() => {
    let submenu = false

    Children.forEach(children, (child: ReactElement) => {
      if (child.type === Menu) {
        submenu = true
      }
    })

    return submenu
  }, [children])
  const isCheckbox = checkbox && !hasSubmenu

  const dataRef = useRef<MenuItemDataRef['current']>({
    checkbox: isCheckbox,
    disabled,
    hasSubmenu,
  })

  const active =
    state.activeItemIndex === null
      ? false
      : state.items[state.activeItemIndex]?.id === id

  const reducers: SubmenuReducers = {
    [SubmenuActionTypes.CloseAll]: () =>
      dispatch({ type: ActionTypes.CloseAll }),
    [SubmenuActionTypes.CloseMenu]: () =>
      dispatch({ type: ActionTypes.CloseSubmenu }),
    [SubmenuActionTypes.OpenMenu]: () =>
      dispatch({ type: ActionTypes.OpenSubmenu, id }),
    [SubmenuActionTypes.RegisterMenu]: (
      action: Extract<
        SubmenuActions,
        { type: SubmenuActionTypes.RegisterMenu }
      >,
    ) => setSubmenuId(action.id),
    [SubmenuActionTypes.UnregisterMenu]: () => setSubmenuId(undefined),
  }

  const runReducer = (action: SubmenuActions) => reducers[action.type](action)

  useEffect(() => {
    if (!active) {
      return
    }

    d.nextFrame(() => {
      document.getElementById(id)?.scrollIntoView?.({ block: 'nearest' })
    })

    return d.dispose
  }, [active, d, id, state.show])

  useEffect(() => {
    dataRef.current.disabled = disabled
  }, [disabled, dataRef])

  useEffect(() => {
    dataRef.current.textValue = document
      .getElementById(id)
      ?.textContent?.toLowerCase()
  }, [id, children, dataRef])

  useEffect(() => {
    dispatch({ type: ActionTypes.RegisterItem, id, dataRef: dataRef })

    return () => dispatch({ type: ActionTypes.UnregisterItem, id })
  }, [dispatch, id, dataRef])

  useEffect(() => {
    if (state.listbox && selected) {
      dispatch({ type: ActionTypes.SelectItem, id, keepShow: true })
    }
  }, [dispatch, id, selected, state.listbox])

  // Focus on the provided element when opening the menu
  useEffect(() => {
    if (state.show && applyFocusRef?.current) {
      applyFocusRef.current.focus()
    }
  }, [applyFocusRef, state.show])

  // Focus on the provided element when it becomes active
  useEffect(() => {
    if (state.show && active && applyFocusRef?.current) {
      applyFocusRef.current.focus()
    }
  }, [active, applyFocusRef, state.show])

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    // ignore cicks on disabled items of the item's submenu
    const target = event.target as Element

    if (
      target.closest &&
      typeof target.closest === 'function' &&
      target.closest('button')?.disabled
    ) {
      return
    }

    // ignore clicks when the event source is not physical descendant
    // this handles the case where we click anywhere (an Item or some empty space) in the submenu
    if (!event.currentTarget.contains(target)) {
      event.stopPropagation()
      return
    }

    // If we already triggered checkbox click, ignore subsequent item click.
    if (isCheckbox) {
      // When clicking outside of checkbox, propagate the click
      if (target instanceof HTMLButtonElement) {
        checkboxRef.current?.click()
      }

      return
    }

    if (hasSubmenu) {
      if (showSubmenu) {
        dispatch({ type: ActionTypes.CloseSubmenu })
      } else {
        dispatch({ type: ActionTypes.OpenSubmenu, id })
      }

      return
    }

    if (selectable && !action) {
      dispatch(
        state.listbox
          ? { type: ActionTypes.SelectItem, id }
          : { type: ActionTypes.CloseAll },
      )

      // only focus on menu button if it's the outermost menu
      if (event.isTrusted && !state.parentRef) {
        createDisposables().nextFrame(() => {
          if (state.headless) {
            state.buttonRef.current?.parentElement?.parentElement?.focus({
              preventScroll: true,
            })
          } else {
            if (state.focusRef?.current) {
              state.focusRef.current?.focus({ preventScroll: true })
            } else {
              state.buttonRef.current?.focus({ preventScroll: true })
            }
          }
        })
      }
    }

    if (onClick) {
      return onClick(event)
    }
  }

  const handleCheckboxClick: ChangeEventHandler<HTMLInputElement> = (event) => {
    toggleChecked()
    onChange?.(event)
  }

  const handleFocus = () => {
    dispatch({ type: ActionTypes.GoToItem, focus: Focus.Specific, id })
  }

  const handleLeave = () => {
    if (disabled) {
      return
    }

    if (!active) {
      return
    }

    dispatch({ type: ActionTypes.GoToItem, focus: Focus.Nothing })
  }

  const handleMove = () => {
    if (disabled || applyFocusRef?.current) {
      return
    }

    if (active) {
      return
    }

    dispatch({ type: ActionTypes.GoToItem, focus: Focus.Specific, id })
  }

  const EndAdornment = hasSubmenu && !endAdornment ? ChevronRight : endAdornment

  // if the item has submenu, it should act as that menu's button
  const menuButtonProps = hasSubmenu
    ? {
        'aria-controls': submenuId,
        'aria-expanded': showSubmenu ? true : undefined,
        'aria-haspopup': true,
      }
    : {}

  return (
    <SubmenuContext.Provider
      value={[{ id, ref, show: showSubmenu }, runReducer]}
    >
      <li
        className={autocomplete === null ? 'max-w-xs' : ''}
        // child elements will have the appropriate role so this takes the <li> out of the accessibility tree
        // and solves issues where certain ARIA roles must be contained by particular parents (and other WCAG violations)
        role="presentation"
      >
        <button
          aria-checked={isCheckbox ? checked : undefined}
          aria-label={label}
          aria-labelledby={labelledBy}
          aria-selected={state.listbox && selected ? 'true' : undefined}
          // TODO: It may be worth refactoring this component.
          // We never apply color to the item.
          // The parameter `action` is ruining the whole style and is never used in the core app.
          // The parameter `selectable` and `selected` does not rely on color.
          className={clsx(
            'flex items-center justify-between w-full text-left whitespace-nowrap active:bg-black-20 hover:bg-black-10',
            action ? 'px-2 py-1' : 'px-5 py-2',
            active && !disabled && selectable && 'ring-inset hover:ring-none',
            selectable && !disabled
              ? 'cursor-pointer'
              : disabled
              ? 'cursor-not-allowed disabled:text-black-30 disabled:bg-transparent'
              : 'cursor-default',
            action && 'rounded',
            action && !disabled && 'text-primary-main',
            selected && 'bg-primary-10',
            color === 'error' && !isCheckbox ? 'text-error-darker' : null,
          )}
          {...toDataSet(dataSet)}
          data-key={key}
          disabled={disabled}
          id={id}
          onClick={handleClick}
          onFocus={handleFocus}
          onMouseLeave={handleLeave}
          onMouseMove={handleMove}
          onPointerLeave={handleLeave}
          onPointerMove={handleMove}
          ref={ref}
          role={
            state.listbox
              ? 'option'
              : isCheckbox
              ? 'menuitemcheckbox'
              : 'menuitem'
          }
          type="button"
          value={value}
          {...menuButtonProps}
        >
          <div
            className={clsx('flex items-center w-full', inset ? 'pl-11' : null)}
          >
            {StartAdornment ? (
              <span className="flex shrink-0 w-6 h-6 mr-5 justify-items-start">
                {typeof StartAdornment === 'function' ? (
                  <StartAdornment />
                ) : (
                  StartAdornment
                )}
              </span>
            ) : null}
            {isCheckbox ? (
              <span className="flex">
                <Checkbox.Input
                  color={color}
                  constrained
                  data-key={key}
                  defaultChecked={defaultChecked}
                  disabled={disabled}
                  inputRef={checkboxRef}
                  name={value}
                  onChange={handleCheckboxClick}
                >
                  <Typography color="inherit" variant="body2">
                    {children}
                  </Typography>
                </Checkbox.Input>
              </span>
            ) : (
              <>
                <span
                  className={clsx(
                    'flex items-center justify-between w-full',
                    wrapText ? 'whitespace-normal break-words' : 'truncate',
                  )}
                >
                  <Typography color="inherit" variant="body2">
                    {children}
                  </Typography>
                </span>
                {selected ? (
                  <span className="shrink-0 w-6 h-6 ml-2">
                    <Done />
                  </span>
                ) : null}
              </>
            )}
          </div>
          {EndAdornment ? (
            <span className="flex shrink-0 w-6 h-6 ml-2">
              <EndAdornment color="inherit" />
            </span>
          ) : null}
        </button>
      </li>
    </SubmenuContext.Provider>
  )
}

export default Item
